import { type ReactNode } from "react";
import { CircleAlertIcon, EllipsisIcon, PlusIcon } from "lucide-react";
import { GoogleIcon, MicrosoftIcon } from "@/objects/@icons";

import { COMMAND_ID as CONNECT_MAILBOX_COMMAND_ID } from "@/app/app/_/commands/ConnectMailboxCommand";
import { ExternalProviderEnum } from "@/__generated__/graphql";
import { HotkeyDecorator } from "@/components/atoms/HotkeyDecorator";
import { Icon } from "@/components/atoms/Icon";
import { PendingSessionProvider } from "@/components/organisms/PendingSession/PendingSession.store";
import { ToolCallCardNodeLayout } from "@/components/organisms/ToolCallCard/ToolCallCardNodeLayout";
import { Mutations, Queries } from "@/constants";
import { SimpleEnum } from "@/components/molecules/SimpleEnum";
import { EnumColors } from "@/constants/values";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { State } from "@/state";
import { Utils } from "@/utils";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import { cn } from "@/components/ui/utils";
import {
  TRIAGE_WAY_ACTIONS_CLASS,
  TriageCardStop,
} from "./TriageChangelistProposalRow";

import {
  TriageDocumentBusinessChip,
  TriageDocumentCard,
  TriageEmailChip,
  TriageLinkBall,
} from "./TriageReconcileRow";
import { TRIAGE_KEYS } from "./TriageView.keys";
import type { TriageOptionListItem } from "./TriageSuggestionsPanel";
import {
  toggleLinesSuggestion,
  useTriageDetailContext,
} from "./TriageView.context";
import { typographyVariants } from "@/components/ui/typography";

/**
 * What picking a way to a payment's document does - a stop on the Match card,
 * by Enter, Space or a click, or an action of Match's menu in the toolbar
 * (``TriageGroupMenus``): a business's forwarding
 * address is copied; a mailbox is searched for the payment's receipt - or, one
 * its provider signed out, signed in to again; the one that connects one opens
 * the dialog that connects one, for the payment's business. A stop still
 * loading does nothing. A stop picked is picked in place of whatever was: there
 * is nothing on it to book.
 */
export function useTriageNoDocumentStop() {
  const {
    triage,
    setActiveOptionKey,
    resolved,
    selections,
    setSelection,
    searchMailbox,
  } = useTriageDetailContext();
  // What a forwarding address takes mail from, said as it is copied: the
  // addresses the person signs in with or added, and their own mailboxes
  // given to the payment's business - one at an address already named, named
  // once. A teammate's shared mailbox is searched, not sent from.
  const business = triage.entity?.identifier;
  const addresses = useBackendQuery(Queries.EMAIL_ADDRESSES_QUERY, {
    fetchPolicy: "cache-first",
  });
  const mailboxes = useBackendQuery(Queries.EMAIL_CONNECTIONS_QUERY, {
    variables: { entityIdentifiers: business ? [business] : [] },
    fetchPolicy: "cache-first",
    skip: !business,
  });
  const senders = [
    ...(addresses.data?.emailAddresses?.data ?? []).map(
      (email) => email.address,
    ),
    ...(mailboxes.data?.emailConnections?.data ?? [])
      .filter(
        (mailbox) =>
          mailbox.ownedByViewer &&
          mailbox.assignments.some(
            (given) => given.entity?.identifier === business,
          ),
      )
      .map((mailbox) => mailbox.emailAddress),
  ].filter(
    (address, index, all) =>
      all.findIndex(
        (other) => other.toLowerCase() === address.toLowerCase(),
      ) === index,
  );
  return (
    stop: TriageOptionListItem,
    {
      isCardAction = false,
    }: {
      /**
       * Picked from its kind's menu in the toolbar, not a stop on a card: it does what it is for
       * alone - the keyboard left where it is, and what was picked kept.
       */
      isCardAction?: boolean;
    } = {},
  ) => {
    // A stop is picked in place of what was: the keyboard on it, and the
    // suggestion picked let go.
    if (!isCardAction) {
      setActiveOptionKey(stop.key);
      if (resolved.linesSuggestion) {
        toggleLinesSuggestion(
          resolved.linesSuggestion,
          selections.linesSuggestionIdentifier,
          setSelection,
        );
      }
    }
    const forwardTo = stop.business;
    if (forwardTo?.address) {
      // Said once the copy has been made, with where to forward from - or
      // that it could not be.
      const from =
        senders.length > 1
          ? `${senders.slice(0, -1).join(", ")} or ${senders[senders.length - 1]}`
          : senders[0];
      void navigator.clipboard.writeText(forwardTo.address).then(
        () =>
          State.getState().setSnackbarMessage(
            `Copied ${forwardTo.name}'s forwarding address` +
              (from ? ` - forward from ${from}` : ""),
          ),
        () =>
          State.getState().setSnackbarMessage(
            `Could not copy ${forwardTo.name}'s forwarding address`,
          ),
      );
    } else if (stop.mailbox?.isSignedOut) {
      if (stop.mailbox.ownedByViewer) {
        State.getState().pushToCommandStack({
          commandDefinitionId: CONNECT_MAILBOX_COMMAND_ID,
          context: {
            entityIdentifier: triage.entity?.identifier,
            reconnectIdentifier: stop.mailbox.identifier,
          },
        });
      }
    } else if (stop.mailbox) {
      searchMailbox(stop.mailbox.identifier);
    } else if (stop.mailbox === null) {
      // Connected where the person is, not a page away, and given to the
      // payment's business so it is searched for it at once.
      State.getState().pushToCommandStack({
        commandDefinitionId: CONNECT_MAILBOX_COMMAND_ID,
        context: { entityIdentifier: triage.entity?.identifier },
      });
    }
  };
}

/**
 * What a search of the viewer's mailboxes for what is typed came to, as a stop
 * on the Match card: each document it found, attached to an email or the email
 * itself, opening to preview beside the others - the three the search puts
 * first and the rest behind a stop that opens them all; that it found nothing,
 * with one more mailbox to connect - or, with none to look in, the way to
 * connect one; that it could not run, to search again; and a mailbox it could
 * not run in, after what the rest found. The mailboxes to search, one more to
 * connect and the forwarding address are in Match's menu in the toolbar
 * (``TriageGroupMenus``).
 */
export function TriageSearchMailboxStop({
  stop,
  isActive = false,
  isLoading = false,
}: {
  stop: TriageOptionListItem;
  isActive?: boolean;
  isLoading?: boolean;
}) {
  const pick = useTriageNoDocumentStop();
  // What is being searched, until the search is back.
  const { searching, setPreviewingEmailConnectionSearchResult, searchMailbox } =
    useTriageDetailContext();

  // A search that could not run: why, as the server said, and to search again
  // - not that nothing was found, for nothing was asked.
  if (stop.searchFailure != null) {
    const words = stop.searchedFor ?? [];
    return (
      <TriageCardStop
        key={stop.key}
        isAction
        stop={stop}
        isActive={isActive}
        isLoading={isLoading}
        leading={
          <div className="flex text-destructive">
            <Icon ReactIcon={CircleAlertIcon} size="sm" />
          </div>
        }
        title={`Couldn't search your mailboxes${
          words.length > 0
            ? ` for ${words.map((word) => `“${word}”`).join(" ")}`
            : ""
        }`}
        description={
          <span className="text-destructive">{stop.searchFailure}</span>
        }
        action="Search again"
        onPress={() => searchMailbox(null, words.join(" "))}
      />
    );
  }

  if (stop.key === "email-connection-search-result:more") {
    return (
      <TriageCardStop
        key={stop.key}
        isAction
        stop={stop}
        isActive={isActive}
        isLoading={isLoading}
        leading={
          <div className="flex text-muted-foreground/80">
            <Icon ReactIcon={EllipsisIcon} size="sm" />
          </div>
        }
        title={stop.title}
        action="Show all"
        // All of them, open on the first of the rest.
        onPress={() =>
          setPreviewingEmailConnectionSearchResult(
            stop.searchResult!.identifier,
          )
        }
      />
    );
  }
  if (stop.searchResult) {
    return (
      <TriageEmailConnectionSearchResultStop
        key={stop.key}
        stop={stop}
        isActive={isActive}
        isLoading={isLoading}
        isOnCard
        // Opened to preview, beside the others: the dialog opens on
        // this one.
        onPress={() =>
          setPreviewingEmailConnectionSearchResult(
            stop.searchResult!.identifier,
          )
        }
      />
    );
  }
  const mailbox = stop.mailbox;
  const isSearching =
    mailbox != null && searching?.connectionIdentifier === mailbox.identifier;
  return (
    <TriageCardStop
      key={stop.key}
      isAction
      stop={stop}
      isActive={isActive}
      isLoading={isLoading}
      leading={
        <div className="flex text-muted-foreground/80">
          {isSearching ? (
            // In its icon's place: the theme's small one.
            <Spinner aria-label={`Searching ${mailbox.emailAddress}`} />
          ) : (
            <Icon
              ReactIcon={
                mailbox === null
                  ? PlusIcon
                  : mailbox?.provider === ExternalProviderEnum.Outlook
                    ? MicrosoftIcon
                    : GoogleIcon
              }
              size="sm"
            />
          )}
        </div>
      }
      title={
        mailbox === null
          ? // Searched for what is typed and nothing found, said so - another
            // offered; else another, beside those there are.
            stop.searchedFor
            ? `Nothing in your ${stop.someUnsearched ? "other " : ""}mailboxes says ${stop.searchedFor
                .map((word) => `“${word}”`)
                .join(" ")}`
            : "Connect Gmail or Outlook"
          : mailbox?.emailAddress
      }
      description={
        mailbox === null ? (
          stop.searchedFor ? (
            "Connect another mailbox"
          ) : null
        ) : isSearching ? (
          "Searching…"
        ) : mailbox?.isSignedOut ? (
          mailbox.ownedByViewer ? (
            "Signed out by its provider: reconnect it to search it"
          ) : (
            `Signed out by its provider: ${mailbox.ownerName ?? "a teammate"} reconnects it`
          )
        ) : // Its last search could not run - the rest went on without it:
        // why, until one of it does.
        mailbox?.searchFailure ? (
          <span className="text-destructive">{mailbox.searchFailure}</span>
        ) : mailbox?.ownedByViewer === false ? (
          `${mailbox.ownerName ?? "A teammate"}'s, shared with you`
        ) : (
          "Yours"
        )
      }
      action={
        mailbox === null
          ? "Connect"
          : mailbox?.isSignedOut
            ? "Reconnect"
            : "Search"
      }
      // Searched, connected or reconnected - by its click, or by
      // Enter. A teammate's signed out is theirs to reconnect.
      onPress={
        !(mailbox?.isSignedOut && !mailbox.ownedByViewer)
          ? () => pick(stop)
          : undefined
      }
    />
  );
}

/** A typical find in a mailbox: three drawn loading while one is searched for what is typed. */
export const TRIAGE_MAILBOX_FIND_PLACEHOLDERS: TriageOptionListItem[] = [
  0, 1, 2,
].map((index) => ({
  key: `email-connection-search-result:placeholder-${index}`,
  sectionId: "email-connection-search-results",
  selectionMode: "single",
  title: "Invoice-2231.pdf",
  selected: false,
  searchResult: {
    id: `placeholder-${index}`,
    identifier: `placeholder-${index}`,
    filename: "Invoice-2231.pdf",
    mimeType: "application/pdf",
    sender: "",
    senderName: "Accounts",
    subject: "Your invoice from Hilltop House",
    sentAt: "2026-06-28T09:15:00Z",
    amount: "1200.00",
    currency: "",
    exchangeRate: null,
    exchangeRateSource: null,
    amountFound: false,
    nameFound: false,
    matches: [],
    isSent: false,
    recipientName: "",
    isPrivate: false,
    isEmail: false,
    mailbox: "you@example.com",
    query: "",
    document: null,
  } as unknown as TriageOptionListItem["searchResult"],
}));

/**
 * A document found in a mailbox, as a stop drawn as the card's suggestions are:
 * the mailbox it is in over its title, and when it came at that line's end;
 * called by its file's name - the email's subject for the email itself - and
 * what it prints at that line's end; under them who sent it - or, one the
 * mailbox sent, whom it went to - and the email it is attached to. One imported
 * before says after its mailbox the document it was filed as, a link to open
 * in the panel - its chip says whether a payment pays it yet - and, on the
 * Match card, one this payment pays has the ball a suggestion picked for it
 * has, on the card's edge. One in a teammate's private mailbox says whose it
 * is, and when. Its card and the dialog it opens to draw it alike.
 */
export function TriageEmailConnectionSearchResultStop({
  stop,
  isActive,
  isLoading = false,
  isOnCard = false,
  onPress,
}: {
  stop: TriageOptionListItem;
  isActive: boolean;
  isLoading?: boolean;
  /** On the Match card, beside its suggestions: one this payment pays has its ball on the card's edge - a list in a dialog has no edge for it. */
  isOnCard?: boolean;
  /** What it does, clicked or by Enter: in its card, opened to preview. */
  onPress?: () => void;
}) {
  const { triage, setPreviewingEmailConnectionSearchResult } =
    useTriageDetailContext();
  const result = stop.searchResult!;
  // Imported before: the document it was filed as - nothing of it from a
  // teammate's private mailbox, which says only whose it is.
  const filed = result.isPrivate ? null : result.document;
  const shares = result.document?.paymentShares ?? [];
  // Paid by this payment - or by its row in the business it is for, which is
  // this payment too, there - said only of one it says where it went of.
  const isPaidByThis =
    filed != null &&
    shares.some(
      (share) =>
        share.transaction.identifier === triage.identifier ||
        share.transaction.identifier === triage.movedToTransaction?.identifier,
    );
  // Its kind, as the match table says it: "Receipt", "Credit note".
  const kind = filed?.documentType
    ? `${filed.documentType.charAt(0)}${filed.documentType.slice(1).toLowerCase().replace(/_/g, " ")}`
    : null;
  // The document it was filed as, opened in the panel by its click - or
  // Enter, or Space, as the keyboard reaches it by Tab - and not the find
  // previewed: out of the dialog it is previewed in, which the panel would be
  // under.
  const openFiled = () => {
    setPreviewingEmailConnectionSearchResult(null);
    State.getState().openNodePanel(filed!.id, { autofocus: false });
  };
  return (
    <TriageCardStop
      stop={stop}
      isActive={isActive}
      isLoading={isLoading}
      card={{
        // The mailbox it is in, and when it came, where a suggestion has its
        // business and its date; then the document it was filed as - in its
        // business, where that is not the payment's - a link of its own.
        crumbs: [
          { label: result.mailbox },
          // Said by its name, or its kind where it has none - nothing of
          // one with neither but its chip.
          ...(filed && (filed.name ?? kind)
            ? [
                {
                  label: (
                    <span data-testid="triage-search-result-filed">
                      {"Filed as "}
                      <span
                        role="link"
                        tabIndex={0}
                        data-testid="triage-search-result-open"
                        onMouseDown={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                          openFiled();
                        }}
                        // Its own key, not the panel's - which would preview
                        // the find.
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            event.stopPropagation();
                            openFiled();
                          }
                        }}
                        className={cn(
                          typographyVariants({
                            level: "body-sm",
                          }),
                          "text-muted-foreground underline underline-offset-4",
                          "inline text-muted-foreground decoration-muted-foreground/80 underline-offset-2 hover:[text-decoration-color:currentColor]",
                        )}
                      >
                        {filed.name ?? kind}
                      </span>
                      {[
                        filed.name ? kind : null,
                        filed.entity.identifier !== triage.entity?.identifier
                          ? filed.entity.name
                          : null,
                      ]
                        .filter((part) => part != null)
                        .map((part) => ` · ${part}`)}
                    </span>
                  ),
                },
              ]
            : []),
        ],
        date: result.sentAt ? Utils.fDate(new Date(result.sentAt)) : null,
        // What it prints, in its own currency where that is not the
        // payment's - nothing of one in a teammate's private mailbox.
        amount:
          result.isPrivate || result.amount == null
            ? null
            : result.currency
              ? `${result.currency} ${Number(result.amount).toFixed(2)}`
              : Utils.fCurrency(result.amount, false, true),
        // Imported before - and paid, in green, where it is: which of several
        // found are in the books already, at a glance.
        titleAfter: result.document ? (
          <span
            data-testid="triage-search-result-imported"
            className="inline-flex align-middle ml-2"
          >
            <SimpleEnum
              label={shares.length > 0 ? "Matched" : "Imported"}
              enumColor={
                shares.length > 0 ? EnumColors.GREEN : EnumColors.GREY_1
              }
              size="sm"
            />
          </span>
        ) : undefined,
        // This payment's, solid on the card's edge, as a suggestion picked for
        // it is - from the stop's ground out to the card's.
        marker:
          isOnCard && isPaidByThis ? (
            <TriageLinkBall
              link={`paid:${result.identifier}`}
              end="to"
              isEngaged
              testId="triage-search-result-paid"
              // Back by the stop's inset (``TRIAGE_STOP_ICON_INSET``).
              className="-ml-1.5"
            />
          ) : undefined,
      }}
      title={
        result.isPrivate
          ? `In ${result.mailbox}'s private mailbox`
          : result.isEmail
            ? result.subject || "No subject"
            : result.filename
      }
      // Who sent it - or whom the mailbox sent it to - and the email it is
      // attached to.
      description={(result.isPrivate
        ? [`${result.mailbox} is asked to confirm it`]
        : [
            result.isSent ? `To ${result.recipientName}` : result.senderName,
            result.isEmail ? null : result.subject,
          ]
      )
        .filter(Boolean)
        .join(" · ")}
      action="Preview"
      onPress={onPress}
    />
  );
}

/**
 * A draft still in review in a chat that the payment would take - a receipt
 * forwarded to its business and read by the AI, say - drawn as a document is
 * wherever it is offered the payment (``TriageDocumentCard``): whose it is and
 * the chat it waits in over "Match “its name”", its date and what it prints at
 * their lines' ends, and who emailed it in on its line of chips. Clicked - or
 * by its chevron, or → - it opens under itself to what it drafted
 * (``TriageDraftMatchDetails``), to be accepted there; Enter picks it. It is
 * not booked as it stands - its tick is out of reach - but as the suggestion it
 * makes once accepted, which takes its place picked; one that settles in
 * another business as well asks there how the two settle it. Confirmed - there
 * or in its chat - it is drawn loading until that suggestion is in. On the one
 * the keyboard is on, Reject - or its key - rejects its change in that chat.
 * One imported from a mailbox is drawn before it is a draft, from what the
 * search said of it, while its chat reads it.
 */
export function TriageDraftMatchStop({
  stop,
  isActive,
  selectionControl,
  disclosure,
}: {
  stop: TriageOptionListItem;
  isActive: boolean;
  /** The tick that picks it, as every suggestion's. */
  selectionControl: ReactNode;
  /** After its title, where it opens to what it drafted: the chevron that opens it. */
  disclosure?: ReactNode;
}) {
  // The payment it would settle: a draft of another business is said to be for it.
  const { triage, setSelection, isSearchFocused } = useTriageDetailContext();
  const draft = stop.draft!;
  const document = draft.document;
  const thread = draft.thread;
  // In another currency than the payment's: its own sign, and what the payment
  // would pay for it, as its suggestion in review weighed it.
  const paidFor = (draft.inReview?.ruleSuggestionDocumentOptions ?? []).find(
    (option) =>
      option.amountDocumentCurrency &&
      option.amountDocumentCurrency !== option.amountTransactionCurrency,
  );
  // Imported from a mailbox and being read in its chat: drawn from what the
  // search said of it - or, opened again, from the file the server says is
  // being read - with nothing to pick or turn down until it is a draft.
  const isReading = stop.searchResult != null || stop.isBeingRead === true;
  // Read with nothing filed for the payment: the chat it was read in, where it
  // is put right.
  const unfiledIn = stop.searchResult != null ? thread : null;
  // Confirmed, it is the suggestion that takes its place.
  const change = document?.pendingChange;
  const isLoading = !change && !isReading;
  // Turned down, its change is rejected in the review it waits in - the chat's
  // others left to it - and it is no longer offered, nor picked.
  const { mutate: rejectPendingNodes } = useBackendMutation({
    mutation: Mutations.REJECT_PENDING_NODES,
    refetchQueries: ["PendingChangeQuery", Queries.TRIAGE_DRAFT_MATCHES_QUERY],
  });
  if (!document?.name) return null;
  return (
    <TriageDocumentCard
      isLoading={isLoading}
      document={{
        name: document.name,
        reference: null,
        date: document.dateIssued ?? null,
        amount:
          document.amountTotal != null ? Number(document.amountTotal) : null,
        currency: paidFor?.amountDocumentCurrency ?? null,
        amountPaid: paidFor ? Number(paidFor.amountTransaction) : null,
        kind: "draft",
        // Whose it is: the business it is drafted in - not yet known while
        // it is read.
        business: document.entity ?? null,
        // Where it stands: the chat it waits in - or, read with nothing
        // filed, the way into the chat it was read in.
        status: unfiledIn ? (
          <>
            {"Its chat filed nothing for it - "}
            {/* Into the chat it was read in, open already or not - a span,
                inside the card's own button. */}
            <span
              role="link"
              tabIndex={-1}
              data-testid="triage-draft-unfiled-fix"
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => {
                event.stopPropagation();
                State.getState().openThreadTab(unfiledIn.id, {
                  label: unfiledIn.name ?? undefined,
                });
              }}
              className="[text-decoration:underline] cursor-pointer text-foreground"
            >
              fix here
            </span>
          </>
        ) : isReading ? (
          "Being read in a chat of its own"
        ) : thread?.name ? (
          `In review in “${thread.name}”`
        ) : (
          "In review"
        ),
      }}
      businessIdentifier={triage.entity?.identifier}
      // Its chevron after its title, where it opens - or, while it is read or
      // read with nothing filed, what it is at.
      titleAfter={
        disclosure ??
        (unfiledIn ? (
          <span
            data-testid="triage-draft-unfiled"
            className="inline-flex align-middle ml-2 text-destructive"
          >
            <Icon ReactIcon={CircleAlertIcon} size="sm" />
          </span>
        ) : isReading ? (
          <span className="inline-flex align-middle ml-2">
            <Spinner aria-label={`Reading ${document.name}`} />
          </span>
        ) : undefined)
      }
      // Whose it is, where another business's - how it is settled there.
      businessChip={
        document.entity &&
        document.entity.identifier !== triage.entity?.identifier ? (
          <TriageDocumentBusinessChip
            documents={[document]}
            business={triage.entity}
            isOut={Number(triage.amount ?? 0) < 0}
          />
        ) : undefined
      }
      toolsAfter={
        isReading ? null : (
          <>
            {/* Who emailed it in - as the payment's row in the queue says. */}
            <TriageEmailChip
              documents={[document]}
              data-testid="triage-draft-email-chip"
            />
            <ButtonGroup className={cn(TRIAGE_WAY_ACTIONS_CLASS, "ml-auto")}>
              {isActive && change ? (
                <Button
                  variant="secondary"
                  size="sm"
                  data-testid="triage-draft-reject"
                  // Its own: turning it down is not a click on the card.
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (stop.selected) {
                      setSelection("draftDocumentIdentifier", null);
                    }
                    void rejectPendingNodes({
                      variables: {
                        input: {
                          sessionIdentifier: change.session.identifier,
                          scopedChangeIdentifiers: [change.identifier],
                        },
                      },
                    });
                  }}
                >
                  Reject
                  <HotkeyDecorator
                    variant="soft"
                    // From the panel's search, after Escape leaves it.
                    hotkey={
                      isSearchFocused
                        ? `escape+${TRIAGE_KEYS.REJECT}`
                        : TRIAGE_KEYS.REJECT
                    }
                    color="inherit"
                  />
                </Button>
              ) : null}
              {selectionControl}
            </ButtonGroup>
          </>
        )
      }
      testIds={{
        crumbs: "triage-card-stop-overline",
        date: "triage-card-stop-overline-end",
        title: "triage-card-stop-title",
        amount: "triage-card-stop-title-end",
        tools: "triage-card-stop-under",
      }}
    />
  );
}

/** What the review's own buttons would do: here its change is decided by Accept and Reject alone. */
const NOTHING = () => {};

/**
 * A draft in review, opened: the record it drafted as the review it waits in
 * has it - drawn as its chat draws it - with Thread, or its key, to open that
 * chat with its changes laid out and put it right there first, and Accept, or
 * its key, to accept it here instead: confirmed and the payment booked by it,
 * in one request. One of another business, which asks how the two settle it,
 * is confirmed alone: the suggestion it makes takes its place picked.
 */
export function TriageDraftMatchDetails({
  item,
}: {
  item: TriageOptionListItem;
}) {
  const { triage } = useTriageDetailContext();
  const document = item.draft?.document;
  const change = document?.pendingChange;
  const thread = item.draft?.thread;
  // Its own change alone, as its card in the chat confirms it: what it builds
  // on comes with it, the chat's others are left to it. The confirm makes the
  // payment's suggestion of it before it returns - booked, or moved to
  // another business first, from there.
  const { mutate: confirmPendingNodes, loading: isConfirming } =
    useBackendMutation({
      mutation: Mutations.CONFIRM_PENDING_NODES,
      refetchQueries: [
        "PendingChangeQuery",
        Queries.TRIAGE_DRAFT_MATCHES_QUERY,
        Queries.TRIAGE_DETAIL_QUERY,
      ],
    });
  if (!document || !change) return null;
  const session = change.session.identifier;
  return (
    <div
      data-testid="triage-draft-details"
      // Under its row, from where the row's title starts.
      className="min-w-0 cursor-default grid grid-cols-[minmax(0,1fr)] gap-y-2 pt-0.5 pl-9 pr-4 pb-4"
    >
      <div className="min-w-0 [border-width:1px] [border-style:solid] border-border rounded-md overflow-clip">
        {/* Read through its review, so what it drafted under it - its lines,
            a contact it made - shows with it. */}
        <PendingSessionProvider
          value={{
            pendingSessionId: session,
            onConfirm: NOTHING,
            onReject: NOTHING,
            isConfirming,
            isRejecting: false,
            onRefetch: NOTHING,
          }}
        >
          <ToolCallCardNodeLayout
            item={{
              identifier: document.identifier,
              __typename: "DocumentObject",
              name: document.name ?? undefined,
            }}
            pendingChangeIdentifier={change.identifier}
            pendingChangeType="create"
            isTypeIconVisible
            typeIconChange={{
              change: "create",
              label: "created",
              enumColor: EnumColors.GREEN,
            }}
            hideRecordTools
            contextEntityIdentifier={triage.entity?.identifier ?? null}
            // Opened to look over before it is taken: all of it, every tab
            // and field, not only what the draft changed.
            defaultShowsRecord
            defaultFieldsExpanded
          />
        </PendingSessionProvider>
      </div>
      {/* Put right in its chat first, or accepted here: the way on, solid. */}
      <ButtonGroup className="[justify-self:end]">
        {thread ? (
          <Button
            variant="outline"
            size="xs"
            data-testid="triage-draft-thread"
            // Its own: opening its chat is not a click on the card above it.
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => {
              event.stopPropagation();
              State.getState().openThreadTab(thread.id, {
                label: thread.name ?? undefined,
                review: true,
              });
            }}
            className="pl-2 pr-1"
          >
            View in thread
            <HotkeyDecorator
              hotkey={TRIAGE_KEYS.DRAFT_THREAD}
              color="inherit"
            />
          </Button>
        ) : null}
        <Button
          size="xs"
          // Nothing is booked of it until it is accepted.
          data-testid="triage-draft-accept"
          // Its own: accepting it is not a click on the card it opened from.
          onMouseDown={(event) => event.preventDefault()}
          // Accepted, it is a record, and the payment's suggestion of it is on
          // its card - Book books it, a step of its own.
          onClick={(event) => {
            event.stopPropagation();
            void confirmPendingNodes({
              variables: {
                input: {
                  sessionIdentifier: session,
                  scopedChangeIdentifiers: [change.identifier],
                },
              },
              // Its suggestion is in as the button lets go.
              awaitRefetchQueries: true,
            });
          }}
          className="pl-2 pr-1"
          disabled={isConfirming}
        >
          {isConfirming && <Spinner />}Accept
          <HotkeyDecorator hotkey={TRIAGE_KEYS.ACCEPT_DRAFT} color="inherit" />
        </Button>
      </ButtonGroup>
    </div>
  );
}
